import { Unit } from '../interfaces/unit';

export class UnitConstants {
  public static readonly QUANTITY = {
    LENGTH: 'LENGTH',
    FORCE: 'FORCE',
    WEIGHT: 'WEIGHT',
    FORCE_BY_LENGTH: 'FORCE_BY_LENGTH',
    ELASTIC_MODULE: 'ELASTIC_MODULE',
    SPECIFIC_WEIGHT: 'SPECIFIC_WEIGHT',
    STIFFNESS_MODULUS: 'STIFFNESS_MODULUS',
    ACCELERATION: 'ACCELERATION',
    ADIMENSIONAL_QUANTITY: 'ADIMENSIONAL_QUANTITY',
    TORSIONAL_STIFFNESS: 'TORSIONAL_STIFFNESS',
    LINEAR_VOLUME: 'LINEAR_VOLUME',
    ANGLE: 'ANGLE',
    TIME: 'TIME',
    VELOCITY: 'VELOCITY',
    MOMENTUM: 'MOMENTUM',
  } as const;

  public static readonly METERS = 'm';
  public static readonly CENTIMETERS = 'cm';
  public static readonly INCHES = 'in';
  public static readonly FOOTS = 'ft';
  public static readonly KILOMETERS = 'km';
  public static readonly MILES = 'mi';
  public static readonly MILLIMETERS = 'mm';

  public static readonly K_NEWTONS = 'kN';
  public static readonly NEWTONS = 'N';
  public static readonly POUNDS = 'lbf';
  public static readonly KG_FORCE = 'kgf';
  public static readonly KIP = 'Kip';

  public static readonly TON = 'Ton';
  public static readonly KG = 'kg';
  public static readonly G = 'g';
  public static readonly MG = 'mg';

  public static readonly NEWTONS_BY_M = 'N/m';
  public static readonly KG_FORCE_M = 'kgf/m';
  public static readonly POUNDS_BY_M = 'lbf/m';

  public static readonly NEWTONS_M2 = 'N.m²';
  public static readonly K_NEWTONS_M2 = 'kN.m²';

  public static readonly K_NEWTONS_BY_M = 'kN/m';
  public static readonly NEWTONS_BY_M2 = 'N/m²';
  public static readonly K_NEWTONS_BY_M2 = 'kN/m²';

  public static readonly NEWTONS_M = 'N.m';
  public static readonly K_NEWTONS_M = 'kN.m';

  public static readonly K_NEWTONS_BY_M3 = 'kN/m³';
  public static readonly KG_FORCE_BY_M3 = 'kgf/m³';
  public static readonly POUNDS_BY_FOOTS_3 = 'lbf/m³';

  public static readonly NEWTONS_M_BY_DEG = 'N.m/deg';
  public static readonly K_NEWTONS_M_BY_DEG = 'kN.m/deg';
  public static readonly NEWTONS_M_BY_RAD = 'N.m/rad';
  public static readonly K_NEWTONS_M_BY_RAD = 'kN.m/rad';

  public static readonly METERS_BY_S2 = 'm/s²';
  public static readonly MILES_BY_S2 = 'mi/s²';

  public static readonly M3_BY_M = 'm³/m';

  public static readonly DEGREE = 'deg';
  public static readonly RAD = 'rad';

  public static readonly MILLISECONDS = 'ms';
  public static readonly SECONDS = 's';
  public static readonly MINUTES = 'min';
  public static readonly HOURS = 'h';
  public static readonly DAYS = 'days';
  public static readonly WEEKS = 'weeks';
  public static readonly MONTHS = 'months';
  public static readonly YEARS = 'years';

  public static readonly METERS_BY_S = 'm/s';
  public static readonly K_METERS_BY_H = 'km/h';
  public static readonly MILES_PER_HOUR = 'mph';

  public static readonly MORISONS_COEFF = {
    INERTIA: `Morison's inertia coefficient`,
    NORMAL_DRAG: `Morison's normal drag coefficient`,
  } as const;

  public static readonly LENGTH = {
    M: { UNIT: UnitConstants.METERS, CONVERSION_FACTOR: 1 },
    KM: {
      UNIT: UnitConstants.KILOMETERS,
      CONVERSION_FACTOR: 1000,
    },
    CM: {
      UNIT: UnitConstants.CENTIMETERS,
      CONVERSION_FACTOR: 0.01,
    },
    IN: {
      UNIT: UnitConstants.INCHES,
      CONVERSION_FACTOR: 0.0254,
    },
    MM: {
      UNIT: UnitConstants.MILLIMETERS,
      CONVERSION_FACTOR: 0.001,
    },
    FT: { UNIT: UnitConstants.FOOTS, CONVERSION_FACTOR: 3.281 },
    MI: {
      UNIT: UnitConstants.MILES,
      CONVERSION_FACTOR: 1609.34,
    },
  } as const;

  public static readonly FORCE = {
    KIP: {
      UNIT: UnitConstants.KIP,
      CONVERSION_FACTOR: 4.4482216,
    },
    NEWTONS: {
      UNIT: UnitConstants.NEWTONS,
      CONVERSION_FACTOR: 0.001,
    },
    K_NEWTONS: {
      UNIT: UnitConstants.K_NEWTONS,
      CONVERSION_FACTOR: 1,
    },
    KG_FORCE: {
      UNIT: UnitConstants.KG_FORCE,
      CONVERSION_FACTOR: 0.00980665,
    },
    POUNDS: {
      UNIT: UnitConstants.POUNDS,
      CONVERSION_FACTOR: 0.0044822,
    },
  } as const;

  public static readonly WEIGHT = {
    TON: { UNIT: UnitConstants.TON, CONVERSION_FACTOR: 1000 },
    KG: { UNIT: UnitConstants.KG, CONVERSION_FACTOR: 1 },
    G: { UNIT: UnitConstants.G, CONVERSION_FACTOR: 0.001 },
    MG: { UNIT: UnitConstants.MG, CONVERSION_FACTOR: 1e-6 },
  } as const;

  public static readonly FORCE_BY_LENGTH = {
    NEWTONS_BY_M: {
      UNIT: UnitConstants.NEWTONS_BY_M,
      CONVERSION_FACTOR: 0.001,
    },
    K_NEWTONS_BY_M: {
      UNIT: UnitConstants.K_NEWTONS_BY_M,
      CONVERSION_FACTOR: 1,
    },
    POUNDS_FORCE_BY_M: {
      UNIT: UnitConstants.POUNDS_BY_M,
      CONVERSION_FACTOR: 0.00444822,
    },
  } as const;

  public static readonly ELASTIC_MODULE = {
    NEWTONS_BY_M2: {
      UNIT: UnitConstants.NEWTONS_BY_M2,
      CONVERSION_FACTOR: 0.001,
    },
    K_NEWTONS_BY_M2: {
      UNIT: UnitConstants.K_NEWTONS_BY_M2,
      CONVERSION_FACTOR: 1,
    },
  } as const;

  public static readonly SPECIFIC_WEIGHT = {
    K_NEWTONS_BY_M3: {
      UNIT: UnitConstants.K_NEWTONS_BY_M3,
      CONVERSION_FACTOR: 1,
    },
    KG_FORCE_BY_M3: {
      UNIT: UnitConstants.KG_FORCE_BY_M3,
      CONVERSION_FACTOR: 0.00980665,
    },
    POUNDS_BY_FOOTS_3: {
      UNIT: UnitConstants.POUNDS_BY_FOOTS_3,
      CONVERSION_FACTOR: 0.15828739917905615,
    },
  } as const;

  public static readonly STIFFNESS_MODULUS = {
    NEWTONS_M2: {
      UNIT: UnitConstants.NEWTONS_M2,
      CONVERSION_FACTOR: 0.001,
    },
    K_NEWTONS_M2: {
      UNIT: UnitConstants.K_NEWTONS_M2,
      CONVERSION_FACTOR: 1,
    },
  } as const;

  public static readonly ACCELERATION = {
    METERS_BY_S2: {
      UNIT: UnitConstants.METERS_BY_S2,
      CONVERSION_FACTOR: 1,
    },
    MILES_BY_S2: {
      UNIT: UnitConstants.MILES_BY_S2,
      CONVERSION_FACTOR: 1609.34,
    },
  } as const;

  public static readonly ADIMENSIONAL_QUANTITY = {
    MORISONS_COEFF_INERTIA: {
      UNIT: UnitConstants.MORISONS_COEFF.INERTIA,
      CONVERSION_FACTOR: 1,
    },
    MORISONS_COEFF_NORMAL_DRAG: {
      UNIT: UnitConstants.MORISONS_COEFF.NORMAL_DRAG,
      CONVERSION_FACTOR: 1,
    },
  } as const;

  public static readonly TORSIONAL_STIFFNESS = {
    NEWTONS_M_BY_DEG: {
      UNIT: UnitConstants.NEWTONS_M_BY_DEG,
      CONVERSION_FACTOR: 0.001,
    },
    NEWTONS_M_BY_RAD: {
      UNIT: UnitConstants.NEWTONS_M_BY_RAD,
      CONVERSION_FACTOR: 1.7453286279273525e-5,
    },
    K_NEWTONS_M_BY_DEG: {
      UNIT: UnitConstants.K_NEWTONS_M_BY_DEG,
      CONVERSION_FACTOR: 1,
    },
    K_NEWTONS_M_BY_RAD: {
      UNIT: UnitConstants.K_NEWTONS_M_BY_RAD,
      CONVERSION_FACTOR: 0.017453286279273523,
    },
  } as const;

  public static readonly LINEAR_VOLUME = {
    M3_BY_M: {
      UNIT: UnitConstants.M3_BY_M,
      CONVERSION_FACTOR: 1,
    },
  } as const;

  public static readonly ANGLE = {
    DEGREE: { UNIT: UnitConstants.DEGREE, CONVERSION_FACTOR: 1 },
    RAD: { UNIT: UnitConstants.RAD, CONVERSION_FACTOR: 57.2958 },
  } as const;

  public static readonly TIME = {
    MILLISECONDS: {
      UNIT: UnitConstants.MILLISECONDS,
      CONVERSION_FACTOR: 0.001,
    },
    SECONDS: {
      UNIT: UnitConstants.SECONDS,
      CONVERSION_FACTOR: 1,
    },
    MINUTES: {
      UNIT: UnitConstants.MINUTES,
      CONVERSION_FACTOR: 60,
    },
    HOURS: {
      UNIT: UnitConstants.HOURS,
      CONVERSION_FACTOR: 3600,
    },
    DAYS: { UNIT: UnitConstants.DAYS, CONVERSION_FACTOR: 86400 },
    WEEKS: {
      UNIT: UnitConstants.WEEKS,
      CONVERSION_FACTOR: 604800,
    },
    MONTHS: {
      UNIT: UnitConstants.MONTHS,
      CONVERSION_FACTOR: 2.628e6,
    },
    YEARS: {
      UNIT: UnitConstants.YEARS,
      CONVERSION_FACTOR: 3.154e7,
    },
  } as const;

  public static readonly VELOCITY = {
    METERS_BY_S: {
      UNIT: UnitConstants.METERS_BY_S,
      CONVERSION_FACTOR: 1,
    },
    K_METERS_BY_H: {
      UNIT: UnitConstants.K_METERS_BY_H,
      CONVERSION_FACTOR: 0.277778,
    },
    MILES_PER_HOUR: {
      UNIT: UnitConstants.MILES_PER_HOUR,
      CONVERSION_FACTOR: 0.44704,
    },
  } as const;

  public static readonly MOMENTUM = {
    NEWTONS_M: {
      UNIT: UnitConstants.NEWTONS_M,
      CONVERSION_FACTOR: 1,
    },
    K_NEWTONS_M: {
      UNIT: UnitConstants.K_NEWTONS_M,
      CONVERSION_FACTOR: 1000,
    },
  } as const;

  public static readonly DEFAULT = {
    LENGTH: {
      reference: 'M',
      value: UnitConstants.LENGTH.M.UNIT,
    } as Unit,
    FORCE: {
      reference: 'K_NEWTONS',
      value: UnitConstants.FORCE.K_NEWTONS.UNIT,
    } as Unit,
    WEIGHT: {
      reference: 'KG',
      value: UnitConstants.WEIGHT.KG.UNIT,
    } as Unit,
    FORCE_BY_LENGTH: {
      reference: 'K_NEWTONS_BY_M',
      value: UnitConstants.FORCE_BY_LENGTH.K_NEWTONS_BY_M.UNIT,
    } as Unit,
    ELASTIC_MODULE: {
      reference: 'K_NEWTONS_BY_M2',
      value: UnitConstants.ELASTIC_MODULE.K_NEWTONS_BY_M2.UNIT,
    } as Unit,
    SPECIFIC_WEIGHT: {
      reference: 'K_NEWTONS_BY_M3',
      value: UnitConstants.SPECIFIC_WEIGHT.K_NEWTONS_BY_M3.UNIT,
    } as Unit,
    STIFFNESS_MODULUS: {
      reference: 'K_NEWTONS_M2',
      value: UnitConstants.STIFFNESS_MODULUS.K_NEWTONS_M2.UNIT,
    } as Unit,
    ACCELERATION: {
      reference: 'METERS_BY_S2',
      value: UnitConstants.ACCELERATION.METERS_BY_S2.UNIT,
    } as Unit,
    ADIMENSIONAL_QUANTITY: {
      reference: 'MORISONS_COEFF_INERTIA',
      value: UnitConstants.ADIMENSIONAL_QUANTITY.MORISONS_COEFF_INERTIA.UNIT,
    } as Unit,
    TORSIONAL_STIFFNESS: {
      reference: 'K_NEWTONS_M_BY_DEG',
      value: UnitConstants.TORSIONAL_STIFFNESS.K_NEWTONS_M_BY_DEG.UNIT,
    } as Unit,
    LINEAR_VOLUME: {
      reference: 'M3_BY_M',
      value: UnitConstants.LINEAR_VOLUME.M3_BY_M.UNIT,
    } as Unit,
    ANGLE: {
      reference: 'DEGREE',
      value: UnitConstants.ANGLE.DEGREE.UNIT,
    } as Unit,
    TIME: {
      reference: 'SECONDS',
      value: UnitConstants.TIME.SECONDS.UNIT,
    } as Unit,
    VELOCITY: {
      reference: 'METERS_BY_S',
      value: UnitConstants.VELOCITY.METERS_BY_S.UNIT,
    } as Unit,
    MOMENTUM: {
      reference: 'K_NEWTONS_M',
      value: UnitConstants.MOMENTUM.K_NEWTONS_M.UNIT,
    } as Unit,
  } as const;
}
